import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Site } from '../../generated/graphql';
import { Box, Typography } from '@mui/material';
import { colors } from '../../styles';
import { useThemeSelector } from '../contexts/ThemeContext';
import { t } from 'i18next';


// Use globally loaded MapLibre GL
declare global {
  interface Window {
    maplibregl: any;
  }
}

interface ParkingMapProps {
  sites: Site[];
  onSiteSelect: (site: Site) => void;
  selectedSite?: Site | null;
}

interface PopupInfo {
  id: string;
  name: string;
  qrCode: string;
  latitude: number;
  longitude: number;
  costPerHour?: number;
  hasMonthly?: boolean;
  hasWeekly?: boolean;
  site?: Site;
}

// New Zealand coordinates as fallback (centered to show the whole country)
const NEW_ZEALAND_COORDS = {
  longitude: 174.0,
  latitude: -41.0,
  zoom: 4
};

const CHRISTCHURCH_COORDS = {
  latitude: -43.5321,
  longitude: 172.6362,
  zoom: 13
};

export const ParkingMap: React.FC<ParkingMapProps> = ({ 
  sites, 
  onSiteSelect, 
  selectedSite 
}) => {
  const map = useRef<any>(null);
  const mapContainer = useRef<HTMLDivElement>(null);
  const { selectedTheme } = useThemeSelector();
  const [viewState, setViewState] = useState(NEW_ZEALAND_COORDS);
  const [userLocation, setUserLocation] = useState<{longitude: number, latitude: number} | null>(null);
  const [popupInfo, setPopupInfo] = useState<PopupInfo | null>(null);
  const markersRef = useRef<any[]>([]);
  const popupRef = useRef<any>(null);

  // Get user location on component mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userCoords = {
            longitude: position.coords.longitude,
            latitude: position.coords.latitude
          };
          setUserLocation(userCoords);
          // Only set view state to user location if no site is selected
          if (!selectedSite) {
            setViewState({
              ...userCoords,
              zoom: 13
            });
          }
        },
        (error) => {
          console.log('Geolocation not available or denied:', error);
          // Keep Christchurch as default center
          setViewState(CHRISTCHURCH_COORDS);
        }
      );
    }
  }, [selectedSite]);

  // Center map on selected site
  useEffect(() => {
    if (selectedSite && selectedSite.latitude && selectedSite.longitude && map.current) {
      map.current.flyTo({
        center: [selectedSite.longitude, selectedSite.latitude],
        zoom: 15
      });
    }
  }, [selectedSite]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current || !window.maplibregl) return;

    map.current = new window.maplibregl.Map({
      container: mapContainer.current,
      style: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
      center: [NEW_ZEALAND_COORDS.longitude, NEW_ZEALAND_COORDS.latitude],
      zoom: NEW_ZEALAND_COORDS.zoom
    });

    // Add navigation control
    map.current.addControl(new window.maplibregl.NavigationControl(), 'top-right');

    // Add markers when map loads
    map.current.on('load', () => {
      addMarkers();
    // Update view when viewState changes
      if (viewState && map.current) {
        map.current.flyTo({
          center: [viewState.longitude, viewState.latitude],
          zoom: 13,
          speed: 2,
          curve: 1,
          essential: true
        });
      }
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [userLocation, viewState]);

  // Add markers to map
  const addMarkers = useCallback(() => {
    if (!map.current || !sites) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Filter sites that have coordinates
    const sitesWithCoordinates = sites.filter(
      site => site.latitude && site.longitude
    );

    // Add user location marker
    if (userLocation) {
      const userMarker = new window.maplibregl.Marker({
        color: colors.plateBlue
      })
        .setLngLat([userLocation.longitude, userLocation.latitude])
        .addTo(map.current);

      userMarker.getElement().addEventListener('click', () => {
        setPopupInfo({
          id: 'user-location',
          name: 'Your Location',
          qrCode: '',
          latitude: userLocation.latitude,
          longitude: userLocation.longitude
        });
      });

      markersRef.current.push(userMarker);
    }

    // Add parking site markers
    sitesWithCoordinates.forEach((site) => {
      const marker = new window.maplibregl.Marker({
        color: selectedSite?.id === site.id ? colors.orange : colors.blue
      })
        .setLngLat([site.longitude!, site.latitude!])
        .addTo(map.current);

      marker.getElement().addEventListener('click', () => {
        handleMarkerClick(site);
      });

      markersRef.current.push(marker);
    });
  }, [sites, userLocation, selectedSite]);

  // Update markers when sites or selected site changes
  useEffect(() => {
    if (map.current && map.current.isStyleLoaded()) {
      addMarkers();
    }
  }, [sites, selectedSite, userLocation, addMarkers]);

  // Handle popup
  useEffect(() => {
  if (!map.current || !popupInfo) {
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
    return;
  }

  if (popupRef.current) {
    popupRef.current.remove();
  }
  
  const popup = new window.maplibregl.Popup({
    closeButton: true,
    closeOnClick: false,
    maxWidth: '300px'
  })
    .setLngLat([popupInfo.longitude, popupInfo.latitude])
    .setHTML(`
      <div style="padding: 8px;">
        <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: bold;">
          ${popupInfo.name}
        </h3>
        <p style="margin: 4px 0; font-size: 14px;">
          <strong>${t("qrCode", { code: popupInfo.qrCode })}</strong>
        </p>
        ${popupInfo.costPerHour ? `
          <p style="margin: 4px 0; font-size: 14px;">
            <strong>${t("hourlyRate", { rate: popupInfo.costPerHour })}</strong>
          </p>
        ` : ''}
        ${popupInfo.hasMonthly ? `
          <p style="margin: 4px 0; font-size: 14px; color: ${colors.orange};">
             <strong>${t("monthlyPassAvailable")}</strong>
          </p>
        ` : ''}
        ${popupInfo.hasWeekly ? `
          <p style="margin: 4px 0; font-size: 14px; color: ${colors.orange};">
             <strong>${t("weeklyPassAvailable")}</strong>
          </p>
        ` : ''}
        ${popupInfo.id !== 'user-location' ? `
          <button id="select-site-btn"
            style="
              margin-top: 8px;
              padding: 6px 12px;
              background: ${selectedTheme?.primaryColor ?? colors.orange};
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
            ">
            ${t("selectSite")}
          </button>
        ` : ''}
      </div>
    `)
    .addTo(map.current);

  popupRef.current = popup;

  const btn = popup.getElement().querySelector("#select-site-btn");
  if (btn) {
    btn.addEventListener("click", () => {
      if (popupInfo.site) {
        onSiteSelect(popupInfo.site);
        popup.remove();
      }
    });
  }

  popup.on("close", () => {
    setPopupInfo(null);
  });

  return () => {
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
  };
}, [popupInfo, onSiteSelect]);

  const handleMarkerClick = useCallback((site: Site) => {
    setPopupInfo({
      id: site.id.toString(),
      name: site.name,
      qrCode: site.qrCode,
      latitude: site.latitude!,
      longitude: site.longitude!,
      costPerHour: site.costPerHour,
      hasMonthly: site.hasMonthly,
      hasWeekly: site.hasWeekly,
      site  
    });
  }, [onSiteSelect]);

  // Check if MapLibre GL is available globally
  if (typeof window !== 'undefined' && window.maplibregl) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box 
          sx={{ 
            height: '400px', 
            width: '100%', 
            borderRadius: '8px', 
            overflow: 'hidden',
            border: `1px solid ${colors.black}`,
            mt: 1
          }}
        >
          <div 
            ref={mapContainer}
            style={{ width: '100%', height: '100%' }}
          />
        </Box>
      </Box>
    );
  }

  // Fallback when MapLibre GL is not available
  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Parking Locations
      </Typography>
      {userLocation && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Your location: {userLocation.latitude.toFixed(4)}, {userLocation.longitude.toFixed(4)}
        </Typography>
      )}
      <Box 
        sx={{ 
          height: '300px', 
          width: '100%', 
          borderRadius: '8px', 
          border: `2px dashed ${colors.black}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.white,
          gap: 2
        }}
      >
        <Typography color="textSecondary" textAlign="center">
          Map loading...
        </Typography>
        <Typography variant="body2" color="textSecondary" textAlign="center">
          Please select a location from the dropdown above
        </Typography>
      </Box>
    </Box>
  );
}; 